import { useEffect, useMemo, useState } from 'react';
import type { ManualEntryStructuredData, ManualEntryCategory, ManualEntryDeadline, ManualEntryFeePrize } from './manualEntryTypes';
import { normaliseStructuredData } from './manualEntryUtils';

interface StructuredDataFormProps {
  data: ManualEntryStructuredData;
  onChange: (data: ManualEntryStructuredData) => void;
}

type StructuredTab = 'categories' | 'deadlines' | 'fees' | 'requirements' | 'contacts' | 'judging';

type RequirementField = keyof ManualEntryStructuredData['requirements'];

export function StructuredDataForm({ data, onChange }: StructuredDataFormProps) {
  const [activeTab, setActiveTab] = useState<StructuredTab>('categories');
  const [localData, setLocalData] = useState<ManualEntryStructuredData>(() => normaliseStructuredData(data));

  useEffect(() => {
    setLocalData(normaliseStructuredData(data));
  }, [data]);

  const update = (next: ManualEntryStructuredData) => {
    setLocalData(next);
    onChange(next);
  };

  const updateCategories = (categories: ManualEntryCategory[]) => {
    update({ ...localData, categories });
  };

  const updateDeadlines = (deadlines: ManualEntryDeadline[]) => {
    update({ ...localData, deadlines });
  };

  const updateFees = (fees: ManualEntryFeePrize[]) => {
    update({ ...localData, fees_prizes: fees });
  };

  const updateRequirements = (requirements: ManualEntryStructuredData['requirements']) => {
    update({ ...localData, requirements });
  };

  const updateContacts = (contacts: ManualEntryStructuredData['contacts']) => {
    update({ ...localData, contacts });
  };

  const updateJudging = (judging: ManualEntryStructuredData['judging']) => {
    update({ ...localData, judging });
  };

  const addCategory = () => {
    updateCategories([
      ...localData.categories,
      { name: '', display_name: '', bisac_code: '', order: localData.categories.length + 1 },
    ]);
  };

  const updateCategory = (index: number, changes: Partial<ManualEntryCategory>) => {
    const categories = localData.categories.map((category, idx) =>
      idx === index ? { ...category, ...changes } : category
    );
    updateCategories(categories);
  };

  const removeCategory = (index: number) => {
    updateCategories(localData.categories.filter((_, idx) => idx !== index));
  };

  const addDeadline = () => {
    updateDeadlines([
      ...localData.deadlines,
      { date: '', type: 'regular', description: '' },
    ]);
  };

  const updateDeadline = (index: number, changes: Partial<ManualEntryDeadline>) => {
    const deadlines = localData.deadlines.map((deadline, idx) =>
      idx === index ? { ...deadline, ...changes } : deadline
    );
    updateDeadlines(deadlines);
  };

  const removeDeadline = (index: number) => {
    updateDeadlines(localData.deadlines.filter((_, idx) => idx !== index));
  };

  const addFee = () => {
    updateFees([
      ...localData.fees_prizes,
      { fee_amount: undefined, prize_amount: undefined, currency: '', tier: '', notes: '' },
    ]);
  };

  const updateFee = (index: number, changes: Partial<ManualEntryFeePrize>) => {
    const fees = localData.fees_prizes.map((fee, idx) =>
      idx === index ? { ...fee, ...changes } : fee
    );
    updateFees(fees);
  };

  const removeFee = (index: number) => {
    updateFees(localData.fees_prizes.filter((_, idx) => idx !== index));
  };

  const updateRequirementField = (key: RequirementField, value: string) => {
    const items = value
      .split('\n')
      .map((item) => item.trim())
      .filter(Boolean);

    updateRequirements({
      ...localData.requirements,
      [key]: items,
    });
  };

  const addContact = () => {
    updateContacts([
      ...localData.contacts,
      { name: '', email: '', role: '', department: '' },
    ]);
  };

  const updateContact = (index: number, changes: Partial<ManualEntryStructuredData['contacts'][number]>) => {
    const contacts = localData.contacts.map((contact, idx) =>
      idx === index ? { ...contact, ...changes } : contact
    );
    updateContacts(contacts);
  };

  const removeContact = (index: number) => {
    updateContacts(localData.contacts.filter((_, idx) => idx !== index));
  };

  const updateCriteria = (value: string) => {
    const criteria = value
      .split('\n')
      .map((item) => item.trim())
      .filter(Boolean);

    updateJudging({
      ...localData.judging,
      criteria,
    });
  };

  const tabs: { key: StructuredTab; label: string }[] = useMemo(
    () => [
      { key: 'categories', label: 'Categories' },
      { key: 'deadlines', label: 'Deadlines' },
      { key: 'fees', label: 'Fees & Prizes' },
      { key: 'requirements', label: 'Requirements' },
      { key: 'contacts', label: 'Contacts' },
      { key: 'judging', label: 'Judging' },
    ],
    []
  );

  const numberInput = (value: number | undefined) => (typeof value === 'number' ? value : '');

  return (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '8px',
      padding: '16px',
      border: '1px solid #e5e7eb'
    }}>
      <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px' }}>
        Structured Data Entry
      </h3>

      <div style={{
        display: 'flex',
        gap: '1px',
        backgroundColor: '#e5e7eb',
        borderRadius: '6px',
        padding: '1px',
        marginBottom: '16px'
      }}>
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              flex: 1,
              padding: '8px 16px',
              border: 'none',
              backgroundColor: activeTab === tab.key ? 'white' : 'transparent',
              borderRadius: '5px',
              cursor: 'pointer',
              fontWeight: activeTab === tab.key ? '500' : '400',
              color: activeTab === tab.key ? '#111827' : '#6b7280',
              fontSize: '14px'
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'categories' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h4 style={{ fontSize: '14px', fontWeight: '500' }}>Award Categories</h4>
            <button
              onClick={addCategory}
              style={{
                padding: '6px 12px',
                backgroundColor: '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                fontSize: '13px',
                cursor: 'pointer'
              }}
            >
              + Add Category
            </button>
          </div>

          {localData.categories.map((category, index) => (
            <div key={`${category.name}-${index}`} style={{
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              padding: '12px',
              marginBottom: '8px'
            }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px', color: '#6b7280' }}>
                    Name (slug)
                  </label>
                  <input
                    placeholder="fiction-general"
                    value={category.name}
                    onChange={(e) => updateCategory(index, { name: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '6px',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      fontSize: '13px'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px', color: '#6b7280' }}>
                    Display Name
                  </label>
                  <input
                    placeholder="General Fiction"
                    value={category.display_name}
                    onChange={(e) => updateCategory(index, { display_name: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '6px',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      fontSize: '13px'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px', color: '#6b7280' }}>
                    BISAC Code
                  </label>
                  <input
                    placeholder="FIC000000"
                    value={category.bisac_code ?? ''}
                    onChange={(e) => updateCategory(index, { bisac_code: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '6px',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      fontSize: '13px'
                    }}
                  />
                </div>
                <div style={{ display: 'flex', gap: '4px', alignItems: 'flex-end' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px', color: '#6b7280' }}>
                      Order
                    </label>
                    <input
                      type="number"
                      value={numberInput(category.order)}
                      onChange={(e) => updateCategory(index, { order: e.target.value ? Number(e.target.value) : undefined })}
                      style={{
                        width: '100%',
                        padding: '6px',
                        border: '1px solid #d1d5db',
                        borderRadius: '4px',
                        fontSize: '13px'
                      }}
                    />
                  </div>
                  <button
                    onClick={() => removeCategory(index)}
                    style={{
                      padding: '6px',
                      backgroundColor: '#fee2e2',
                      color: '#dc2626',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    ✕
                  </button>
                </div>
              </div>
            </div>
          ))}

          {localData.categories.length === 0 && (
            <div style={{
              textAlign: 'center',
              padding: '32px',
              color: '#6b7280',
              backgroundColor: '#f9fafb',
              borderRadius: '6px'
            }}>
              No categories added yet. Click "Add Category" to begin.
            </div>
          )}
        </div>
      )}

      {activeTab === 'deadlines' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h4 style={{ fontSize: '14px', fontWeight: '500' }}>Deadlines</h4>
            <button
              onClick={addDeadline}
              style={{
                padding: '6px 12px',
                backgroundColor: '#2563eb',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                fontSize: '13px',
                cursor: 'pointer'
              }}
            >
              + Add Deadline
            </button>
          </div>

          {localData.deadlines.map((deadline, index) => (
            <div key={`${deadline.date}-${index}`} style={{
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              padding: '12px',
              marginBottom: '8px'
            }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px', color: '#6b7280' }}>
                    Date (YYYY-MM-DD)
                  </label>
                  <input
                    placeholder="2025-01-31"
                    value={deadline.date}
                    onChange={(e) => updateDeadline(index, { date: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '6px',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      fontSize: '13px'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px', color: '#6b7280' }}>
                    Type
                  </label>
                  <select
                    value={deadline.type}
                    onChange={(e) => updateDeadline(index, { type: e.target.value as ManualEntryDeadline['type'] })}
                    style={{
                      width: '100%',
                      padding: '6px',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      fontSize: '13px',
                      backgroundColor: 'white'
                    }}
                  >
                    <option value="early">Early</option>
                    <option value="regular">Regular</option>
                    <option value="final">Final</option>
                    <option value="rolling">Rolling</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div style={{ display: 'flex', gap: '4px', alignItems: 'flex-end' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px', color: '#6b7280' }}>
                      Notes
                    </label>
                    <input
                      placeholder="Postmarked by midnight"
                      value={deadline.description ?? ''}
                      onChange={(e) => updateDeadline(index, { description: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '6px',
                        border: '1px solid #d1d5db',
                        borderRadius: '4px',
                        fontSize: '13px'
                      }}
                    />
                  </div>
                  <button
                    onClick={() => removeDeadline(index)}
                    style={{
                      padding: '6px',
                      backgroundColor: '#fee2e2',
                      color: '#dc2626',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    ✕
                  </button>
                </div>
              </div>
            </div>
          ))}

          {localData.deadlines.length === 0 && (
            <div style={{
              textAlign: 'center',
              padding: '32px',
              color: '#6b7280',
              backgroundColor: '#f9fafb',
              borderRadius: '6px'
            }}>
              No deadlines captured yet. Click "Add Deadline" to track date milestones.
            </div>
          )}
        </div>
      )}

      {activeTab === 'fees' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h4 style={{ fontSize: '14px', fontWeight: '500' }}>Fees & Prizes</h4>
            <button
              onClick={addFee}
              style={{
                padding: '6px 12px',
                backgroundColor: '#f97316',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                fontSize: '13px',
                cursor: 'pointer'
              }}
            >
              + Add Row
            </button>
          </div>

          {localData.fees_prizes.map((fee, index) => (
            <div key={`${fee.tier}-${index}`} style={{
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              padding: '12px',
              marginBottom: '8px'
            }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '8px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px', color: '#6b7280' }}>
                    Entry Fee
                  </label>
                  <input
                    type="number"
                    placeholder="75"
                    value={numberInput(fee.fee_amount)}
                    onChange={(e) => updateFee(index, { fee_amount: e.target.value ? Number(e.target.value) : undefined })}
                    style={{
                      width: '100%',
                      padding: '6px',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      fontSize: '13px'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px', color: '#6b7280' }}>
                    Prize Amount
                  </label>
                  <input
                    type="number"
                    placeholder="5000"
                    value={numberInput(fee.prize_amount)}
                    onChange={(e) => updateFee(index, { prize_amount: e.target.value ? Number(e.target.value) : undefined })}
                    style={{
                      width: '100%',
                      padding: '6px',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      fontSize: '13px'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px', color: '#6b7280' }}>
                    Currency
                  </label>
                  <input
                    placeholder="USD"
                    value={fee.currency ?? ''}
                    onChange={(e) => updateFee(index, { currency: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '6px',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      fontSize: '13px'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px', color: '#6b7280' }}>
                    Tier / Notes
                  </label>
                  <input
                    placeholder="Early bird, Student, Grand prize"
                    value={fee.tier ?? ''}
                    onChange={(e) => updateFee(index, { tier: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '6px',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      fontSize: '13px'
                    }}
                  />
                </div>
                <div style={{ display: 'flex', gap: '4px', alignItems: 'flex-end' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px', color: '#6b7280' }}>
                      Additional Notes
                    </label>
                    <input
                      placeholder="Includes feedback for finalists"
                      value={fee.notes ?? ''}
                      onChange={(e) => updateFee(index, { notes: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '6px',
                        border: '1px solid #d1d5db',
                        borderRadius: '4px',
                        fontSize: '13px'
                      }}
                    />
                  </div>
                  <button
                    onClick={() => removeFee(index)}
                    style={{
                      padding: '6px',
                      backgroundColor: '#fee2e2',
                      color: '#dc2626',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    ✕
                  </button>
                </div>
              </div>
            </div>
          ))}

          {localData.fees_prizes.length === 0 && (
            <div style={{
              textAlign: 'center',
              padding: '32px',
              color: '#6b7280',
              backgroundColor: '#f9fafb',
              borderRadius: '6px'
            }}>
              No fee or prize information yet. Click "Add Row" to capture entry fees or award payouts.
            </div>
          )}
        </div>
      )}

      {activeTab === 'requirements' && (
        <div style={{ display: 'grid', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '13px', marginBottom: '4px', fontWeight: '500' }}>
              Eligibility Requirements
            </label>
            <textarea
              placeholder="Enter each requirement on a new line..."
              value={localData.requirements.eligibility.join('\n')}
              onChange={(e) => updateRequirementField('eligibility', e.target.value)}
              style={{
                width: '100%',
                minHeight: '120px',
                padding: '8px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                fontSize: '13px',
                resize: 'vertical'
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '13px', marginBottom: '4px', fontWeight: '500' }}>
              Restrictions
            </label>
            <textarea
              placeholder="Award limitations, excluded genres, previous winners, etc."
              value={localData.requirements.restrictions.join('\n')}
              onChange={(e) => updateRequirementField('restrictions', e.target.value)}
              style={{
                width: '100%',
                minHeight: '100px',
                padding: '8px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                fontSize: '13px',
                resize: 'vertical'
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '13px', marginBottom: '4px', fontWeight: '500' }}>
              Submission Types
            </label>
            <textarea
              placeholder="Hardcover, eBook, Audiobook, Manuscript sample, etc."
              value={localData.requirements.submission_types.join('\n')}
              onChange={(e) => updateRequirementField('submission_types', e.target.value)}
              style={{
                width: '100%',
                minHeight: '100px',
                padding: '8px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                fontSize: '13px',
                resize: 'vertical'
              }}
            />
          </div>
        </div>
      )}

      {activeTab === 'contacts' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h4 style={{ fontSize: '14px', fontWeight: '500' }}>Contacts</h4>
            <button
              onClick={addContact}
              style={{
                padding: '6px 12px',
                backgroundColor: '#9333ea',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                fontSize: '13px',
                cursor: 'pointer'
              }}
            >
              + Add Contact
            </button>
          </div>

          {localData.contacts.map((contact, index) => (
            <div key={`${contact.email}-${index}`} style={{
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              padding: '12px',
              marginBottom: '8px'
            }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px', color: '#6b7280' }}>
                    Name
                  </label>
                  <input
                    value={contact.name}
                    onChange={(e) => updateContact(index, { name: e.target.value })}
                    placeholder="Jane Doe"
                    style={{
                      width: '100%',
                      padding: '6px',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      fontSize: '13px'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px', color: '#6b7280' }}>
                    Email
                  </label>
                  <input
                    value={contact.email ?? ''}
                    onChange={(e) => updateContact(index, { email: e.target.value })}
                    placeholder="awards@example.org"
                    style={{
                      width: '100%',
                      padding: '6px',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      fontSize: '13px'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px', color: '#6b7280' }}>
                    Role
                  </label>
                  <input
                    value={contact.role ?? ''}
                    onChange={(e) => updateContact(index, { role: e.target.value })}
                    placeholder="Program Director"
                    style={{
                      width: '100%',
                      padding: '6px',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      fontSize: '13px'
                    }}
                  />
                </div>
                <div style={{ display: 'flex', gap: '4px', alignItems: 'flex-end' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px', color: '#6b7280' }}>
                      Department
                    </label>
                    <input
                      value={contact.department ?? ''}
                      onChange={(e) => updateContact(index, { department: e.target.value })}
                      placeholder="Awards Office"
                      style={{
                        width: '100%',
                        padding: '6px',
                        border: '1px solid #d1d5db',
                        borderRadius: '4px',
                        fontSize: '13px'
                      }}
                    />
                  </div>
                  <button
                    onClick={() => removeContact(index)}
                    style={{
                      padding: '6px',
                      backgroundColor: '#fee2e2',
                      color: '#dc2626',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    ✕
                  </button>
                </div>
              </div>
            </div>
          ))}

          {localData.contacts.length === 0 && (
            <div style={{
              textAlign: 'center',
              padding: '32px',
              color: '#6b7280',
              backgroundColor: '#f9fafb',
              borderRadius: '6px'
            }}>
              No contacts recorded. Add at least one program contact for follow-up questions.
            </div>
          )}
        </div>
      )}

      {activeTab === 'judging' && (
        <div style={{ display: 'grid', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '13px', marginBottom: '4px', fontWeight: '500' }}>
              Judging Process Summary
            </label>
            <textarea
              placeholder="Outline how submissions are evaluated, key phases, and panel composition."
              value={localData.judging.process ?? ''}
              onChange={(e) => updateJudging({ ...localData.judging, process: e.target.value })}
              style={{
                width: '100%',
                minHeight: '120px',
                padding: '8px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                fontSize: '13px',
                resize: 'vertical'
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '13px', marginBottom: '4px', fontWeight: '500' }}>
              Judging Criteria (one per line)
            </label>
            <textarea
              placeholder="Literary merit\nOriginality\nProduction quality"
              value={localData.judging.criteria.join('\n')}
              onChange={(e) => updateCriteria(e.target.value)}
              style={{
                width: '100%',
                minHeight: '120px',
                padding: '8px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                fontSize: '13px',
                resize: 'vertical'
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
